import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const IDEMPOTENCY_KEY_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$";
const SHARE_KEY_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$";
const SPLIT_NUMBER_PATTERN = "^FD-[0-9]{8}-[0-9A-F]{12}$";

export const CheckoutSplitAllocationModeSchema = Type.Union([
  Type.Literal("BY_AMOUNT"),
  Type.Literal("BY_ITEM"),
]);
export type CheckoutSplitAllocationMode = Static<typeof CheckoutSplitAllocationModeSchema>;

export const CheckoutSplitManualPaymentMethodSchema = Type.Union([
  Type.Literal("CASH"),
  Type.Literal("STATIC_WECHAT"),
  Type.Literal("STATIC_ALIPAY"),
]);
export type CheckoutSplitManualPaymentMethod = Static<
  typeof CheckoutSplitManualPaymentMethodSchema
>;

export const CheckoutSplitItemAllocationSchema = Type.Object(
  {
    itemId: Type.String({ minLength: 1, maxLength: 128 }),
    quantity: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type CheckoutSplitItemAllocation = Static<typeof CheckoutSplitItemAllocationSchema>;

export const CheckoutSplitShareDraftSchema = Type.Object(
  {
    shareKey: Type.String({ pattern: SHARE_KEY_PATTERN }),
    payerLabel: Type.String({ minLength: 1, maxLength: 30 }),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    otherAmountFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    itemAllocations: Type.Array(CheckoutSplitItemAllocationSchema, { maxItems: 10_000 }),
  },
  { additionalProperties: false },
);
export type CheckoutSplitShareDraft = Static<typeof CheckoutSplitShareDraftSchema>;

export const SaveCheckoutSplitPlanRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    expectedOrderId: Type.String({ minLength: 1, maxLength: 128 }),
    expectedTotalFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedPlanVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    allocationMode: CheckoutSplitAllocationModeSchema,
    shares: Type.Array(CheckoutSplitShareDraftSchema, { minItems: 2, maxItems: 8 }),
    idempotencyKey: Type.String({ pattern: IDEMPOTENCY_KEY_PATTERN }),
  },
  { additionalProperties: false },
);
export type SaveCheckoutSplitPlanRequest = Static<typeof SaveCheckoutSplitPlanRequestSchema>;

export const CheckoutSplitShareStatusSchema = Type.Union([
  Type.Literal("UNPAID"),
  Type.Literal("PAID"),
]);
export type CheckoutSplitShareStatus = Static<typeof CheckoutSplitShareStatusSchema>;

export const CheckoutSplitShareReadModelSchema = Type.Object(
  {
    shareId: Type.String({ minLength: 1, maxLength: 256 }),
    shareKey: Type.String({ pattern: SHARE_KEY_PATTERN }),
    allocationOrder: Type.Integer({ minimum: 1, maximum: 8 }),
    payerLabel: Type.String({ minLength: 1, maxLength: 30 }),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    otherAmountFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    itemAllocations: Type.Array(CheckoutSplitItemAllocationSchema, { maxItems: 10_000 }),
    status: CheckoutSplitShareStatusSchema,
    paymentId: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    paymentMethod: Type.Union([CheckoutSplitManualPaymentMethodSchema, Type.Null()]),
    paidAt: Type.Union([Type.String({ minLength: 20, maxLength: 32 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type CheckoutSplitShareReadModel = Static<typeof CheckoutSplitShareReadModelSchema>;

export const CheckoutSplitPlanReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    planId: Type.String({ minLength: 1, maxLength: 128 }),
    splitNumber: Type.String({ pattern: SPLIT_NUMBER_PATTERN }),
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    allocationMode: CheckoutSplitAllocationModeSchema,
    status: Type.Union([
      Type.Literal("DRAFT"),
      Type.Literal("IN_PROGRESS"),
      Type.Literal("COMPLETED"),
    ]),
    totalFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    paidFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    remainingFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    shares: Type.Array(CheckoutSplitShareReadModelSchema, { minItems: 2, maxItems: 8 }),
    createdBy: Type.String({ minLength: 1, maxLength: 128 }),
    createdAt: Type.String({ minLength: 20, maxLength: 32 }),
    updatedAt: Type.String({ minLength: 20, maxLength: 32 }),
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type CheckoutSplitPlanReadModel = Static<typeof CheckoutSplitPlanReadModelSchema>;

export const PayCheckoutSplitShareRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    planId: Type.String({ minLength: 1, maxLength: 128 }),
    shareId: Type.String({ minLength: 1, maxLength: 256 }),
    expectedPlanVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedShareAmountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    method: CheckoutSplitManualPaymentMethodSchema,
    idempotencyKey: Type.String({ pattern: IDEMPOTENCY_KEY_PATTERN }),
  },
  { additionalProperties: false },
);
export type PayCheckoutSplitShareRequest = Static<typeof PayCheckoutSplitShareRequestSchema>;

function safeSum(values: readonly number[]): number | null {
  let sum = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    sum += value;
    if (!Number.isSafeInteger(sum)) return null;
  }
  return sum;
}

function isCanonicalTimestamp(value: string): boolean {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value;
}

function draftsAreConsistent(
  allocationMode: CheckoutSplitAllocationMode,
  shares: readonly CheckoutSplitShareDraft[],
  expectedTotalFen: number,
): boolean {
  const shareKeys = new Set<string>();
  const labels = new Set<string>();
  for (const share of shares) {
    const normalizedLabel = share.payerLabel.trim();
    const itemIds = new Set<string>();
    if (
      normalizedLabel !== share.payerLabel ||
      labels.has(normalizedLabel) ||
      shareKeys.has(share.shareKey) ||
      share.itemAllocations.some(
        (allocation) => itemIds.has(allocation.itemId) || !itemIds.add(allocation.itemId),
      ) ||
      (allocationMode === "BY_AMOUNT" &&
        (share.itemAllocations.length !== 0 || share.otherAmountFen !== share.amountFen))
    ) {
      return false;
    }
    labels.add(normalizedLabel);
    shareKeys.add(share.shareKey);
  }
  return safeSum(shares.map((share) => share.amountFen)) === expectedTotalFen;
}

export function isSaveCheckoutSplitPlanRequest(
  value: unknown,
): value is SaveCheckoutSplitPlanRequest {
  return (
    Value.Check(SaveCheckoutSplitPlanRequestSchema, value) &&
    draftsAreConsistent(
      (value as SaveCheckoutSplitPlanRequest).allocationMode,
      (value as SaveCheckoutSplitPlanRequest).shares,
      (value as SaveCheckoutSplitPlanRequest).expectedTotalFen,
    )
  );
}

export function isCheckoutSplitPlanReadModel(value: unknown): value is CheckoutSplitPlanReadModel {
  if (!Value.Check(CheckoutSplitPlanReadModelSchema, value)) return false;
  const plan = value as CheckoutSplitPlanReadModel;
  const paymentIds = plan.shares.flatMap((share) =>
    share.paymentId === null ? [] : [share.paymentId],
  );
  if (
    !isCanonicalTimestamp(plan.createdAt) ||
    !isCanonicalTimestamp(plan.updatedAt) ||
    Date.parse(plan.updatedAt) < Date.parse(plan.createdAt) ||
    new Set(plan.shares.map((share) => share.shareId)).size !== plan.shares.length ||
    new Set(paymentIds).size !== paymentIds.length ||
    plan.shares.some((share, index) => {
      const paidFieldsPresent =
        share.paymentId !== null && share.paymentMethod !== null && share.paidAt !== null;
      const unpaidFieldsAbsent =
        share.paymentId === null && share.paymentMethod === null && share.paidAt === null;
      return (
        share.allocationOrder !== index + 1 ||
        (share.status === "PAID" ? !paidFieldsPresent : !unpaidFieldsAbsent) ||
        (share.paidAt !== null && !isCanonicalTimestamp(share.paidAt))
      );
    })
  ) {
    return false;
  }
  const drafts = plan.shares.map(
    ({ shareKey, payerLabel, amountFen, otherAmountFen, itemAllocations }) => ({
      shareKey,
      payerLabel,
      amountFen,
      otherAmountFen,
      itemAllocations,
    }),
  );
  const paidFen = safeSum(
    plan.shares.filter((share) => share.status === "PAID").map((share) => share.amountFen),
  );
  if (
    !draftsAreConsistent(plan.allocationMode, drafts, plan.totalFen) ||
    paidFen === null ||
    plan.paidFen !== paidFen ||
    plan.remainingFen !== plan.totalFen - paidFen
  ) {
    return false;
  }
  const expectedStatus =
    paidFen === 0 ? "DRAFT" : paidFen === plan.totalFen ? "COMPLETED" : "IN_PROGRESS";
  return plan.status === expectedStatus;
}

export function isPayCheckoutSplitShareRequest(
  value: unknown,
): value is PayCheckoutSplitShareRequest {
  return Value.Check(PayCheckoutSplitShareRequestSchema, value);
}
